import { inject, Injectable } from '@angular/core';
import { mapAgentGroupingTypeToProfileType, ProfileType } from '@rma/generic/domain/types';
import { CurrentUser, CurrentUserAgency, CurrentUserAgent, CurrentUserService } from '@rma/generic/ng/user';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { map, Observable } from 'rxjs';
import { UserType } from '../../entities/profile-user.model';

export interface AccessibleProfile {
  profileCode: string;
  profileType: ProfileType;
  userType: 'Agent' | 'Agency';
  profileName: string;
  profileImage: string;
  // Remove when we remove the LegacyUserService
  index: number | null;
  backgroundColour?: string;
  primaryColour?: string;
  email?: string;
  claimedAt?: Date;
  firstname?: string;
  lastname?: string;
  parentName?: string;
}

/**
 * ProfileAccessService
 *
 * Provides methods to retrieve the profiles  by `profileType` or `UserType` and `ProfileCode` accessible to the current user.
 *
 * - Exposes `getUsersAccessibleProfiles(filters)`, which returns an observable of all accessible profiles for the current user, optionally filtered by profile type.
 *   - The current user's profile is always returned. Regardless of whether 'Individual' is passed in as a filter or not.
 * - Exposes `getUsersAccessibleProfile(profileType, profileCode)`, which returns an observable of a specific accessible profile (agent or agency) by type and code, or `null` if not found.
 *   - Overloads ensure correct typing: returns `CurrentUserAgent` for 'Individual' or 'Agent', and `CurrentUserAgency` for 'Office', 'Team', or 'Agency'.
 *
 * @remarks
 * - This is not currently used, but i want to add it soon.
 * - The current implementation is a stub for a potential api in the future.
 * - This service will allow us to remove references to the CurrentUserService in the UI layer.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileAccessContextService {
  private readonly currentUserService = inject(CurrentUserService);

  public getUsersAccessibleProfiles(profileTypes: ProfileType[] = ['Individual', 'Office', 'Team']): Observable<AccessibleProfile[]> {
    return this.currentUserService.currentUser$.pipe(
      nonNullable(),
      map((currentUser) => {
        const includeNonCurrentUserAgentProfiles = profileTypes.includes('Individual');
        const agencyTypeInclusions = profileTypes.filter((profileType) => profileType === 'Team' || profileType === 'Office');

        const individualProfiles = this.getIndividualProfilesForCurrentUser(currentUser, includeNonCurrentUserAgentProfiles);
        const agencyProfiles = this.getAgencyProfilesForCurrentUser(currentUser, agencyTypeInclusions);

        return [...individualProfiles, ...agencyProfiles].sort((a, b) => a.profileName.localeCompare(b.profileName));
      }),
    );
  }

  public getUsersAccessibleProfile(profileType: ProfileType | UserType, profileCode: string): Observable<AccessibleProfile | null> {
    return this.currentUserService.currentUser$.pipe(
      nonNullable(),
      map((currentUser) => {
        if (profileType === 'Individual' || profileType === 'Agent') {
          const individual =
            currentUser?.agent && currentUser.agent.agentCode === profileCode
              ? this.mapUserToAccessibleProfile(currentUser.agent, currentUser)
              : null;
          return individual;
        } else {
          const currentAgencies = currentUser?.agencies ?? [];
          const index = currentAgencies.findIndex((a) => a.agencyCode === profileCode && a.agentGroupingType === profileType);
          const agency = currentAgencies[index];
          return !agency ? null : this.mapAgencyToAccessibleProfile(agency, index);
        }
      }),
    );
  }

  private getIndividualProfilesForCurrentUser(currentUser: CurrentUser, includeNonCurrentUserAgentProfiles: boolean): AccessibleProfile[] {
    const onlyCurrentUserProfile: AccessibleProfile[] = currentUser.agent
      ? [this.mapUserToAccessibleProfile(currentUser.agent, currentUser)]
      : [];
    return includeNonCurrentUserAgentProfiles
      ? (currentUser.agents?.reduce((accum, agent) => {
          const agentIsCurrentUser = onlyCurrentUserProfile.length > 0 && onlyCurrentUserProfile[0].profileCode === agent.agentCode;
          return agentIsCurrentUser ? accum : [...accum, this.mapAgentToAccessibleProfile(agent)];
        }, onlyCurrentUserProfile) ?? onlyCurrentUserProfile)
      : onlyCurrentUserProfile;
  }

  private getAgencyProfilesForCurrentUser(currentUser: CurrentUser, agencyProfileTypes: ProfileType[]): AccessibleProfile[] {
    const initialAgencyProfiles: AccessibleProfile[] = [];
    return (
      currentUser.agencies?.reduce((accum, agency, index) => {
        const agencyProfile = this.mapAgencyToAccessibleProfile(agency, index);
        return agencyProfileTypes.includes(agencyProfile.profileType) ? [...accum, agencyProfile] : accum;
      }, initialAgencyProfiles) ?? initialAgencyProfiles
    );
  }

  private mapUserToAccessibleProfile(individual: CurrentUserAgent, user: CurrentUser): AccessibleProfile {
    return {
      ...this.mapAgentToAccessibleProfile(individual),
      profileImage: user.profileImage,
      profileName: user.name ?? user.alias,
      email: user.email,
    };
  }

  private mapAgentToAccessibleProfile(individual: CurrentUserAgent): AccessibleProfile {
    return {
      index: null,
      profileCode: individual.agentCode,
      profileImage: individual.profileImage ?? '',
      profileName: individual.name ?? '',
      profileType: 'Individual',
      userType: 'Agent',
    };
  }

  private mapAgencyToAccessibleProfile(agency: CurrentUserAgency, index: number): AccessibleProfile {
    return {
      backgroundColour: agency.backgroundColour,
      primaryColour: agency.primaryColour,
      index,
      profileCode: agency.agencyCode,
      profileImage: agency.profileImage,
      profileName: agency.name,
      profileType: mapAgentGroupingTypeToProfileType(agency.agentGroupingType),
      userType: 'Agency',
    };
  }
}
